import { useEffect, useMemo } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";

import { mainTabActions } from "@/Redux/store";
import { audienceFromPath, isCompany } from "@/utils/generalUrils";
import { useI18n } from "@/i18n/I18nProvider";

import { AUDIENCE, isCompanyProduct } from "./navbar.config";
import { DESKTOP_MEDIA_QUERY } from "./navbar.styles";

/*
 * True on the server and on the first client render so the markup matches;
 * corrected right after mount. Only used to unmount the (already CSS-hidden)
 * desktop bar on phones — never to decide what the first paint looks like.
 */
export function useDesktopViewport() {
  return useMediaQuery(DESKTOP_MEDIA_QUERY, { defaultMatches: true });
}

/*
 * Which experience the visitor is browsing — individuals or companies.
 *
 * Most URLs say so on their own, and those are resolved during render so the
 * server and the client produce the same markup. The rest (pages reachable
 * from both sides, such as /about or /blogs) fall back to the redux `mainTab`
 * slice, which remembers the side the visitor came from and is what pages
 * outside the navbar read too. The effect keeps that slice in step with the
 * URL — `isCompany()` also persists the choice, so it cannot run during render.
 */
export function useAudience() {
  const dispatch = useDispatch();
  const router = useRouter();
  const rememberedAudience = useSelector((state) => state.mainTab.mainTab);

  const audienceOfPath = audienceFromPath(router.asPath);

  const audience =
    audienceOfPath === null
      ? rememberedAudience
      : audienceOfPath
        ? AUDIENCE.companies
        : AUDIENCE.individuals;

  useEffect(() => {
    dispatch(
      mainTabActions.update(
        isCompany() ? AUDIENCE.companies : AUDIENCE.individuals,
      ),
    );
  }, [dispatch, router.asPath]);

  const selectAudience = (next) => dispatch(mainTabActions.update(next));

  return {
    audience,
    isCompanyAudience: audience === AUDIENCE.companies,
    selectAudience,
  };
}

/*
 * The products shown in the financing menus, on desktop and in the drawer
 * alike. `allProduct` comes from the page's data fetching; the localStorage
 * copy written by _app is the fallback for pages that do not fetch it.
 */
export function useNavProducts(allProduct, { isCompanyAudience }) {
  const { locale } = useI18n();

  /* The cached copy is ~90 KB of JSON; parsing it once per open is enough. */
  return useMemo(() => {
    const source =
      Array.isArray(allProduct) && allProduct.length > 0
        ? allProduct
        : readCachedProducts();

    return source
      .filter((product) =>
        isCompanyAudience
          ? isCompanyProduct(product)
          : !isCompanyProduct(product),
      )
      .map((product) => ({
        id: product.id,
        url: product.path?.startsWith("/")
          ? product.path
          : `/products/${product.name_en}`,
        img: product.icon || "/services/car.svg",
        title: locale === "en" ? product.name_en : product.name_ar,
      }));
  }, [allProduct, isCompanyAudience, locale]);
}

function readCachedProducts() {
  if (typeof window === "undefined") return [];

  try {
    const cached = JSON.parse(localStorage.getItem("allProducts") || "[]");
    return Array.isArray(cached) ? cached : [];
  } catch {
    return [];
  }
}
